import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import AdminTable from '../../../components/AdminTable';
import EmptyRecord from '../../../components/EmptyRecord';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import { GET_REALTOR_DETAILS_BY_AGENT_ID } from '../../../graphql/admin_queries';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../customHooks/useTableColumn';
import useCustomQuery from '../../../network/useCustomQuery';
import useToast from '../../../customHooks/useToast';
import generateCSV from '../../../utils/generateCSV';
import generatePDF from '../../../utils/generatePdf';
import { cleanUpRealtorsDetail } from '../../../utils/helpers';

import Styles from './RealtorReferralTableRecord.module.scss';

const limit = 14;
interface IRewardDetailsTableRecord {
  referralId: string;
}

const RewardDetailsTableRecord = ({
  referralId,
}: IRewardDetailsTableRecord) => {
  const { agentId } = useParams<{ agentId: string }>();
  const [getRealtorsByAgentId, realtorRefereesLoading, realtorReferees] = useCustomQuery({
    query: GET_REALTOR_DETAILS_BY_AGENT_ID,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();
  const Toast = useToast();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'numberOfProperties',
    'numberOfTenants',
    'dateOnboarded',
    'status',
  ];

  const columnLabelFormat = {
    name: 'Name of Landlord',
    numberOfProperties: 'No. of Uploaded Properties',
    numberOfTenants: 'No. of Invited Tenants',
    dateOnboarded: 'Date Onboarded',
    status: 'Status',
  };
  const { getColumns } = useTableColumn(allColumns, 'reward');
  const [trxRec, setrxRec] = useState(null);

  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`/rewards/realtor-details/${agentId}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    const result = getFilterQueries();
    push(
      `/rewards/realtor-details/${agentId}?page=1&search=${encodeURIComponent(
        e.target.value,
      )}&fromDateTable=${result?.fromDateTable}&toDateTable=${
        result?.toDateTable
      }`,
    );
  };

  useEffect(() => {
    if (referralId && page) {
      const result = getFilterQueries();
      getRealtorsByAgentId({
        variables: {
          offset: (page - 1) * limit,
          limit,
          agentId: referralId,
          // ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          from: result.fromDateTable,
          to: result.toDateTable,
        },
      });
    }
  }, [page, search, referralId]);

  const getRealtorDetailFunc = (result) => {
    getRealtorsByAgentId({
      variables: {
        offset: 0,
        limit,
        search,
        agentId: referralId,
        from: result.fromDateTable,
        to: result.toDateTable,
        // ...(tabStatus ? {} : { status: result.status }),
      },
    });
  };

  const onFilterSubmit = (result) => {
    if (referralId) {
      getRealtorDetailFunc(result);
    }
  };

  const { filterObj } = useFilterMethods({
    allowedFilters: ['tableDateRange'],
    defaultUrl: `/rewards/realtor-details/${agentId}?page=1`,
    cb: onFilterSubmit,
    Toast,
  });

  useEffect(() => {
    if (realtorReferees?.getRealtorsByAgentId?.data) {
      setrxRec(realtorReferees?.getRealtorsByAgentId);
    }
  }, [realtorReferees]);

  const [getExportRecord] = useCustomQuery({
    query: GET_REALTOR_DETAILS_BY_AGENT_ID,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          agentId: referralId,
          // ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          from: result.fromDateTable,
          to: result.toDateTable,
          status: result.status,
        },
      });

      if (output?.data?.getRealtorsByAgentId) {
        const rec = cleanUpRealtorsDetail(
          output?.data?.getRealtorsByAgentId?.data,
        );
        console.log(rec, 'rec');
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name'
            ? ['name', 'email']
            : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Realtor Details',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.wrapper__header}>
        <DashboardHeaderWithFilters
          title="Referral statistics"
          filterObj={filterObj}
          dateType="tableDateRange"
          showSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Customer name /  Email address"
          exportObj={{
            handleExport,
          }}
        />
      </div>

      <div className="transaction-table-details-cover">
        {trxRec?.data?.count ? (
          <AdminTable
            refreshing={realtorRefereesLoading}
            page={page}
            setPage={setPage}
            pageSize={limit}
            minHeight="calc(100vh - 280px)"
            count={trxRec?.data?.count}
            contents={cleanUpRealtorsDetail(trxRec?.data?.realtors)}
            reference="id"
            hideDominantImage
            hasNameAndImage
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={[
              'status',
              ['name', 'email'],
              ['numberOfProperties', 'numberOfTenants', 'dateOnboarded'],
            ]}
            formatLabels={columnLabelFormat}
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 240px)"
            title={
              realtorRefereesLoading ? 'Fetching records...' : 'No records yet'
            }
            main="All Landlord referrals from this Realtor on Payfi will appear here"
            // footer="Receive Payments"
            loading={realtorRefereesLoading}
            Icon={EmptyMerchantSettlement}
          />
        )}
      </div>
    </div>
  );
};

export default RewardDetailsTableRecord;
